import { SourceDataEvent } from "@/api/dtos";
import useDeleteSourceDataEvent from "@/hooks/data/useDeleteSourceDataEvent";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle } from "@mui/material";
import { useSnackbar } from "notistack";
import { FC, useCallback, useState } from "react";

interface ISourceDataEventDeleteDialogProps extends DialogProps {
    sourceDataEvent?: SourceDataEvent;
    handleClose: (success: boolean) => any;
    onAfterSubmit?: (...params: any) => any | Promise<any>;
    preventCloseOnSubmit?: boolean;
}

const SourceDataEventDeleteDialog: FC<ISourceDataEventDeleteDialogProps> = ({
    sourceDataEvent,
    handleClose,
    onAfterSubmit,
    preventCloseOnSubmit,
    ...props
}) => {
    const [submitting, setSubmitting] = useState(false);
    const deleteEvent = useDeleteSourceDataEvent();
    const snackbar = useSnackbar();

    const handleSubmit = useCallback(async () => {
        if(!deleteEvent) {
            return;
        }
        
        setSubmitting(true);

        try {
            if(deleteEvent && sourceDataEvent) {
                const response = await deleteEvent(sourceDataEvent);
    
                if(response?.responseStatus?.errors && response?.responseStatus?.errors.length > 0) {
                    snackbar.enqueueSnackbar({
                        variant: "error",
                        message: "Something went wrong when attempting to delete the event."
                    });
                }
                else {
                    snackbar.enqueueSnackbar({
                        variant: "success",
                        message: "Event deleted successfully."
                    });
                }
            }
    
            if(onAfterSubmit) {
                await onAfterSubmit();
            }
    
            setSubmitting(false);
    
            if(!preventCloseOnSubmit) {
                handleClose(true);
            }
        }
        catch(e) {
            setSubmitting(false);

            snackbar.enqueueSnackbar({
                variant: "error",
                message: "Something went wrong when attempting to delete the event."
            });
        }
    }, [deleteEvent, onAfterSubmit, handleClose, preventCloseOnSubmit, sourceDataEvent]);

    if(!sourceDataEvent) {
        return null;
    }

    return (
        <Dialog
            {...props}
        >
            <DialogTitle>Confirm Event Deletion</DialogTitle>
            <DialogContent>
                This will set the event and all of its forms to "Not Started".
                Are you sure you want to continue?
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => handleClose(false)}
                >
                    Cancel
                </Button>
                <Button
                    disabled={submitting}
                    onClick={handleSubmit}
                >
                    {
                        submitting ? 
                            <CircularProgress /> :
                            "Confirm"
                    }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default SourceDataEventDeleteDialog;